import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import Highlighter from 'react-highlight-words';
import { Trans, useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from './apps/Home/components/TemporalBanner';
import { ReactComponent as At } from './assets/img/icons/at.svg';
import { ReactComponent as Hash } from './assets/img/icons/hash.svg';
import { ReactComponent as UserSettingsInfo } from './assets/img/icons/user-settings-info.svg';
import { ReactComponent as CMS } from './assets/img/icons/user-settings-lang.svg';
import { ReactComponent as UserSettingsName } from './assets/img/icons/user-settings-name.svg';
import { ReactComponent as LogoFull } from './assets/logo_blue.svg';
import { ReactComponent as LogoB } from './assets/logo_m_blue.svg';
import { ReactComponent as LogoW } from './assets/logo_m_white.svg';
import { ReactComponent as LogoFullWhite } from './assets/logo_white.svg';
import { ReactComponent as Burger } from './assets/svg/Burger.svg';
import { ReactComponent as HeadingCatalogProducer } from './assets/svg/Catalog.svg';
import { ReactComponent as Close } from './assets/svg/CloseSearch.svg';
import { ReactComponent as Consumer } from './assets/svg/Consumer.svg';
import { ReactComponent as Lion } from './assets/svg/Lion.svg';
import { ReactComponent as Notify } from './assets/svg/Notify.svg';
import { ReactComponent as HeadIcon } from './assets/svg/PermRequest.svg';
import { ReactComponent as Plus } from './assets/svg/Plus.svg';
import { ReactComponent as Search } from './assets/svg/Search.svg';
import { ReactComponent as SignIn } from './assets/svg/SingIn.svg';
import { ReactComponent as TV } from './assets/svg/TV.svg';
import { ReactComponent as Tools } from './assets/svg/Tools.svg';
import { ReactComponent as Unicorn } from './assets/svg/Unicorn.svg';
import { ReactComponent as User } from './assets/svg/User.svg';
import { ReactComponent as Star } from './assets/svg/content/status-fav.svg';
import { ReactComponent as Producer } from './assets/svg/header-producer.svg';
import { ReactComponent as HeadingCatalog } from './assets/svg/heading-catalog.svg';
import { ReactComponent as Help } from './assets/svg/help.svg';
import { ReactComponent as UserList } from './assets/svg/list-users.svg';
import { ReactComponent as Logout } from './assets/svg/logout.svg';
import { ReactComponent as Bell } from './assets/svg/notification.svg';
import { ROLE, useIsAdmin, useIsConsumer, useIsGuest, useIsProvider, useIsSiteEditor } from './components/common';
import Button from './components/mplat/Button/Button';
import TableListName from './components/mplat/DataTable/TableListName';
import { Modal } from './components/mplat/Modal';
import { FeedbackModal } from './components/mplat/Modals/FeedbackModal';
import { GuestAttachModal } from './components/mplat/Modals/GuestAttachModal';
import { LoginModal } from './components/mplat/Modals/LoginModal';
import { useAcc, useAuth, useLanguage, useRole } from './hooks';
import { NotificationItem } from './pages/NotificationListPage';
import { useSuLogoutMutation } from './services/api';
import {
    ContentType,
    useLazyGetAccessRequestsQuery,
    useLazyGetContentListQuery,
} from './services/content_v1';
import { useLazyGetNotificationsUserQuery } from './services/notifications';
import { AppHeaderProps } from './types/types';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const SEARCH_LIMIT = 5;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_CLOSE_ANIMATION_MS = 500;
const CLIP_PATH_MIN_DURATION = 0.2;
const CLIP_PATH_MAX_DURATION = 0.5;
const CLIP_PATH_DURATION_DIVISOR = 3000;
const CLIP_PATH_FOCUS_DELAY_MS = 1;

const CONSUMER_ROLES = [
    ROLE.CONSUMER_ADMIN,
    ROLE.CONSUMER_AUDITOR,
    ROLE.CONSUMER_CONTENT_MANAGER,
    ROLE.CONSUMER_ENGINEER,
    ROLE.CONSUMER_OWNER,
] as const;

const PROVIDER_ROLES = [
    ROLE.PROVIDER_ADMIN,
    ROLE.PROVIDER_AUDITOR,
    ROLE.PROVIDER_CONTENT_MANAGER,
    ROLE.PROVIDER_ENGINEER,
    ROLE.PROVIDER_OWNER,
] as const;

const SITE_ROLES = [
    ROLE.SITE_ADMIN,
    ROLE.SITE_AUDITOR,
    ROLE.SITE_MODERATOR,
] as const;

const PARTNER_MODERATOR_ROLES = [
    ROLE.PARTNER_MODERATOR_ADMIN,
    ROLE.PARTNER_MODERATOR_OWNER,
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function getDiagonal(): number {
    const { clientWidth, clientHeight } = document.documentElement;
    return Math.hypot(clientWidth, clientHeight);
}

function getClipPathPosition(element: HTMLElement) {
    const w = getDiagonal();
    return {
        x: element.offsetLeft + element.clientWidth - 14,
        y: element.offsetTop + element.clientHeight / 2,
        w,
        t: clamp(w / CLIP_PATH_DURATION_DIVISOR, CLIP_PATH_MIN_DURATION, CLIP_PATH_MAX_DURATION),
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// getUserIcon
// ─────────────────────────────────────────────────────────────────────────────

function getUserIcon(role: ROLE, className?: string) {
    if (CONSUMER_ROLES.includes(role as typeof CONSUMER_ROLES[number])) {
        return <User className={className} />;
    }
    if (PROVIDER_ROLES.includes(role as typeof PROVIDER_ROLES[number])) {
        return <Producer className={className} />;
    }
    if (SITE_ROLES.includes(role as typeof SITE_ROLES[number])) {
        return <Unicorn className={className} />;
    }
    if (PARTNER_MODERATOR_ROLES.includes(role as typeof PARTNER_MODERATOR_ROLES[number])) {
        return <Lion className={className} />;
    }
    return <User className={className} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface NavLinkItemProps {
    item: {
        link: string;
        noend?: boolean;
        icon?: React.ReactNode;
        label?: string;
        data?: React.ReactNode;
    };
}

function NavLinkItem({ item }: NavLinkItemProps) {
    return (
        <li>
            <NavLink
                to={item.link}
                end={!item.noend}
                className={({ isActive }) =>
                    isActive ? 'header__nav-link _active' : 'header__nav-link'
                }
            >
                {item.icon}
                {item.label}
                {item.data ? (
                    <span className="header__nav-link-bubble">{item.data}</span>
                ) : null}
            </NavLink>
        </li>
    );
}

interface HighlightedTextProps {
    text: string;
    search: string;
}

function HighlightedText({ text, search }: HighlightedTextProps) {
    return (
        <Highlighter
            highlightClassName="search__results-highlight"
            searchWords={[search]}
            autoEscape
            highlightTag="span"
            textToHighlight={text}
        />
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// AppHeader
// ─────────────────────────────────────────────────────────────────────────────

function AppHeader({ items }: AppHeaderProps) {
    const [sulogout] = useSuLogoutMutation();
    const [searchContent, { data: findedContent }] = useLazyGetContentListQuery();
    const [searchAccess, { data: findedAccess }] = useLazyGetAccessRequestsQuery();
    const [searchNotify, { data: findedNotify }] = useLazyGetNotificationsUserQuery();

    const auth = useAuth();
    const role = useRole();
    const acc = useAcc();
    const { i18n, t } = useTranslation();
    const lang = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 400px)');

    const isAdmin = useIsAdmin();
    const isSiteEditor = useIsSiteEditor();
    const isConsumer = useIsConsumer();
    const isProvider = useIsProvider();
    const isGuest = useIsGuest();

    const prefferedNotSet = useMemo(
        () => !(acc?.prefferedSourceTV || acc?.prefferedSourceUnit || acc?.prefferedSourceServices),
        [acc],
    );

    const refSearch = useRef<HTMLDivElement | null>(null);
    const refSearchInput = useRef<HTMLInputElement | null>(null);
    const [searchMode, setSearchMode] = useState(false);
    const [realSearchMode, setRealSearchMode] = useState(false);
    const [searchPosition, setSearchPosition] = useState({ x: 0, y: 0, w: 0, t: CLIP_PATH_MIN_DURATION });
    const [accessRequested, setAccessRequested] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [attachDialogOpen, setAttachDialogOpen] = useState(false);
    const [showLogin, setShowLogin] = useState(false);

    const changeLanguage = useCallback(
        (lng: string) => i18n.changeLanguage(lng),
        [i18n],
    );

    // ── Debounce the search input ────────────────────────────────────────────
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    // ── Fetch search results when the debounced query changes ────────────────
    const hasAccount = !!auth.account;

    useEffect(() => {
        if (hasAccount && debouncedSearch) {
            searchContent({ offset: 0, limit: SEARCH_LIMIT, search: debouncedSearch, header: 1 });
            searchAccess({ offset: 0, limit: SEARCH_LIMIT, search: debouncedSearch, header: 1 });
            searchNotify({ offset: 0, limit: SEARCH_LIMIT, search: debouncedSearch, header: 1 });
        }
    }, [hasAccount, debouncedSearch, searchAccess, searchContent, searchNotify]);

    // ── Animate the search overlay clip-path ─────────────────────────────────
    useEffect(() => {
        setSearchPosition((s) => ({ ...s, w: 0 }));

        if (searchMode) {
            setRealSearchMode(true);
            if (refSearch.current) {
                const pos = getClipPathPosition(refSearch.current);
                setTimeout(() => {
                    setSearchPosition(pos);
                    refSearchInput.current?.focus();
                }, CLIP_PATH_FOCUS_DELAY_MS);
            }
        } else {
            if (refSearch.current) {
                const pos = {
                    x: refSearch.current.offsetLeft + refSearch.current.clientWidth - 14,
                    y: refSearch.current.offsetTop + refSearch.current.clientHeight / 2,
                    w: 0,
                    t: CLIP_PATH_MIN_DURATION,
                };
                setSearchPosition(pos);
            }
            setTimeout(() => {
                setSearch('');
                setRealSearchMode(false);
            }, SEARCH_CLOSE_ANIMATION_MS);
        }
    }, [searchMode]);

    // ── Reset search when switching to a different page ──────────────────────
    useEffect(() => {
        setSearchMode(false);
    }, [location.pathname]);

    // ── Keyboard: close search on Escape ────────────────────────────────────
    useEffect(() => {
        if (!searchMode) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setSearchMode(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [searchMode]);

    // ── Derived helpers ──────────────────────────────────────────────────────
    const toggleLanguage = useCallback(() => {
        changeLanguage(lang === 'ru' ? 'en' : 'ru');
    }, [changeLanguage, lang]);

    const handleSuLogout = useCallback(() => {
        if (!auth.suUser || !auth.suRefresh) return;
        sulogout({ refresh: auth.suRefresh, username: auth.suUser })
            .unwrap()
            .catch(() => navigate('/logout'));
    }, [auth.suUser, auth.suRefresh, sulogout, navigate]);

    const handleCreateClick = useCallback(() => {
        const { prefferedSourceTV: tv, prefferedSourceUnit: unit, prefferedSourceServices: svc } = acc ?? {};
        const sources = [tv, unit, svc].filter(Boolean).length;

        if (sources >= 2) {
            setCreateDialogOpen(true);
        } else if (tv) {
            navigate('/content/tv/create');
        } else if (unit) {
            navigate('/content/media/create');
        } else if (svc) {
            navigate('/content/service/create');
        } else {
            setCreateDialogOpen(true);
        }
    }, [acc, navigate]);

    const closeSearchAndNavigate = useCallback(
        (path: string) => {
            navigate(path);
            setSearchMode(false);
        },
        [navigate],
    );

    // ── Render helpers ───────────────────────────────────────────────────────
    const renderLogo = () => {
        const showCompact = auth.user && (location.pathname !== '/' || isMobile);
        if (showCompact) {
            return (
                <Link to="/">
                    {isConsumer
                        ? <LogoW style={{ width: '28px', height: '30px' }} />
                        : <LogoB style={{ width: '28px', height: '30px' }} />}
                </Link>
            );
        }
        return isConsumer
            ? <LogoFullWhite style={{ width: '149px', height: '30px' }} />
            : (
                <Link to="/">
                    <LogoFull style={{ width: '149px', height: '30px' }} />
                </Link>
            );
    };

    const renderNavLinks = () => (
        <ul>
            {isGuest && (
                <li>
                    <a href={`/${lang}/about`} className="header__nav-link">
                        <Trans>About</Trans>
                    </a>
                </li>
            )}
            <li>
                <a href={`/${lang}/public-news`} className="header__nav-link">
                    <Trans>News</Trans>
                </a>
            </li>
            {items.map((item) => (
                <NavLinkItem key={item.link} item={item} />
            ))}
        </ul>
    );

    const renderGuestNavLinks = () => (
        <ul>
            <li>
                <a href={`/${lang}/about`} className="header__nav-link">
                    <Trans>About</Trans>
                </a>
            </li>
            <li>
                <a href={`/${lang}/public-news`} className="header__nav-link">
                    <Trans>News</Trans>
                </a>
            </li>
            <li>
                <a href={`/${lang}/public-showcase/media`} className="header__nav-link">
                    <Trans>Showcase</Trans>
                </a>
            </li>
            <li>
                <a href={`/${lang}/public-promo`} className="header__nav-link">
                    <Trans>cms:PROMO</Trans>
                </a>
            </li>
            <li>
                <a href={`/${lang}/public-event`} className="header__nav-link">
                    <Trans>cms:EVENT</Trans>
                </a>
            </li>
            <li>
                <Link to="/help" className="header__nav-link">
                    <Trans>Help</Trans>
                </Link>
            </li>
        </ul>
    );

    const renderCreateMenu = () => (
        <div className="header__create-menu">
            <GuestAttachModal isOpen={attachDialogOpen} onClose={() => setAttachDialogOpen(false)} />
            <Modal
                isOpen={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                minWidth={300}
                className="popup_tv-add"
            >
                <div className="header__create-menu">
                    <p className="header__create-menu-heading">
                        <Trans>Create a new card</Trans>
                    </p>
                    {!!(prefferedNotSet || acc?.prefferedSourceUnit) && (
                        <p className="header__create-menu-item">
                            <Button onClick={() => { setCreateDialogOpen(false); navigate('/content/media/create'); }}>
                                <Plus style={{ verticalAlign: 'baseline' }} /> <Trans>Content unit</Trans>
                            </Button>
                        </p>
                    )}
                    {!!(prefferedNotSet || acc?.prefferedSourceTV) && (
                        <p className="header__create-menu-item">
                            <Button onClick={() => { setCreateDialogOpen(false); navigate('/content/tv/create'); }}>
                                <Plus style={{ verticalAlign: 'baseline' }} /> <Trans>TV channel</Trans>
                            </Button>
                        </p>
                    )}
                    {!!(prefferedNotSet || acc?.prefferedSourceServices) && (
                        <p className="header__create-menu-item">
                            <Button onClick={() => { setCreateDialogOpen(false); navigate('/content/service/create'); }}>
                                <Plus style={{ verticalAlign: 'baseline' }} /> <Trans>content:SERVICE</Trans>
                            </Button>
                        </p>
                    )}
                </div>
            </Modal>
            {isProvider && (
                <Button
                    className="btn header__create-btn"
                    data-test-id="createContentButton"
                    onClick={handleCreateClick}
                >
                    <Plus style={{ verticalAlign: 'baseline' }} />
                    <span className="header__create-label">
                        <Trans>Create</Trans>
                    </span>
                </Button>
            )}
            {isGuest && (
                <Button
                    className="btn header__create-btn"
                    data-test-id="createContentButton"
                    onClick={() => setAttachDialogOpen(true)}
                >
                    <Plus style={{ verticalAlign: 'baseline' }} />
                    <span className="header__create-label">
                        <Trans>Create</Trans>
                    </span>
                </Button>
            )}
        </div>
    );

    const renderUserMenu = () => (
        <div className="header__user-item _menu">
            <Burger />
            <div className="header__user-menu">
                <Link to="/help" className="header__user-menu-item">
                    <Help /> <Trans>Support</Trans>
                </Link>
                {auth.account && (
                    <>
                        <Link to="/profile" className="header__user-menu-item">
                            <UserSettingsInfo /> <Trans>account:USER</Trans>
                        </Link>
                        <Link to="/select-accounts" className="header__user-menu-item">
                            <At /> <Trans>My accounts</Trans>
                        </Link>
                        <Link to="/users" className="header__user-menu-item">
                            <UserList /> <Trans>Users</Trans>
                        </Link>
                        <Link to="/tools" className="header__user-menu-item">
                            <Tools /> <Trans>Tools</Trans>
                        </Link>
                        <a href={`/${lang}/public-promo`} className="header__user-menu-item">
                            <CMS /> <Trans>cms:PROMO</Trans>
                        </a>
                        <a href={`/${lang}/public-event`} className="header__user-menu-item">
                            <CMS /> <Trans>cms:EVENT</Trans>
                        </a>
                        {isProvider && (
                            <Link to="/consumers" className="header__user-menu-item">
                                <Consumer /> <Trans>Participants</Trans>
                            </Link>
                        )}
                        {isConsumer && (
                            <Link to="/partners" className="header__user-menu-item">
                                <Consumer /> <Trans>Participants</Trans>
                            </Link>
                        )}
                        {(isAdmin || isProvider) && (
                            <Link to="/links" className="header__user-menu-item">
                                <Hash /> <Trans>content:PUBLIC_LINKS</Trans>
                            </Link>
                        )}
                        {isAdmin && (
                            <Link to="/accounts" className="header__user-menu-item">
                                <At /> <Trans>Accounts</Trans>
                            </Link>
                        )}
                        {isAdmin && (
                            <Link to="/organizations" className="header__user-menu-item">
                                <UserSettingsName /> <Trans>Organizations</Trans>
                            </Link>
                        )}
                        {isAdmin && (
                            <Link to="/group" className="header__user-menu-item">
                                <Hash /> <Trans>Groups</Trans>
                            </Link>
                        )}
                        {isAdmin && (
                            <Link to="/tvdb" className="header__user-menu-item">
                                <TV /> <Trans>MM</Trans>
                            </Link>
                        )}
                        {isAdmin && (
                            <Link to="/yell" className="header__user-menu-item">
                                <Producer /> <Trans>Accounts</Trans> Yell
                            </Link>
                        )}
                        {isAdmin && (
                            <Link to="/system-settings" className="header__user-menu-item">
                                <Star /> <Trans>Settings</Trans>
                            </Link>
                        )}
                        {isAdmin && (
                            <Link to="/dictionaries" className="header__user-menu-item">
                                <Star /> <Trans>Dictionaries</Trans>
                            </Link>
                        )}
                        {isAdmin && (
                            <Link to="/notifications/system" className="header__user-menu-item">
                                <Bell /> <Trans>Notifications</Trans>
                            </Link>
                        )}
                        {(isAdmin || isSiteEditor) && (
                            <Link to="/cmsys" className="header__user-menu-item">
                                <CMS /> <Trans>cms:CMS</Trans>
                            </Link>
                        )}
                    </>
                )}
                <Link to="/logout" className="header__user-menu-item _logout">
                    <Logout /> <Trans>Exit</Trans>
                </Link>
                {auth.suUser && (
                    <span
                        className="header__user-menu-item _logout"
                        onClick={handleSuLogout}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSuLogout(); } }}
                    >
                        <Logout /> {auth.user}
                    </span>
                )}
            </div>
        </div>
    );

    const renderGuestBurgerMenu = () => (
        <div className="header__user-item _menu">
            <Burger />
            <div className="header__user-menu">
                <Link to="/about" className="header__user-menu-item">
                    <At /> <Trans>About</Trans>
                </Link>
                <a href={`/${lang}/public-news`} className="header__user-menu-item">
                    <Hash /> <Trans>News</Trans>
                </a>
                <a href={`/${lang}/public-showcase/media`} className="header__user-menu-item">
                    <HeadingCatalogProducer /> <Trans>Showcase</Trans>
                </a>
                <Link to="/help" className="header__user-menu-item">
                    <Help /> <Trans>Header menu support</Trans>
                </Link>
            </div>
        </div>
    );

    const renderSearchAccessResults = () => {
        if (!findedAccess?.results.count) return null;
        return (
            <section className="search__results-section _orders" style={{ opacity: 1, transform: 'translate(0px)' }}>
                <h1 className="search__results-heading">
                    <HeadIcon />
                    <Trans>Orders</Trans>
                </h1>
                {findedAccess.results.data?.map((unit) => (
                    <div
                        key={unit.id}
                        style={{ cursor: 'pointer' }}
                        className="search__results-item"
                        onClick={() => closeSearchAndNavigate('/deals/orders')}
                    >
                        <strong>
                            <HighlightedText
                                search={debouncedSearch}
                                text={isConsumer
                                    ? `${unit.consumer.owner.firstname} ${unit.consumer.owner.lastname}`
                                    : unit.consumer.name || ''}
                            />
                        </strong>
                        <br />
                        <HighlightedText
                            search={debouncedSearch}
                            text={`${unit.consumer.owner.firstname} ${unit.consumer.owner.lastname}`}
                        />
                        &nbsp;·{' '}
                        <a href={`tel:${unit.consumer?.owner.phone}`}>{unit.consumer?.owner.phone}</a>
                        {' · '}
                        <a href={`mailto:${unit.consumer.owner.email}`} className="link-underline">
                            {unit.consumer.owner.email}
                        </a>
                    </div>
                ))}
            </section>
        );
    };

    const renderSearchContentResults = () => {
        if (!findedContent?.results.count) return null;
        return (
            <section className="search__results-section _catalog" style={{ opacity: 1, transform: 'translate(0px)' }}>
                <h1 className="search__results-heading">
                    {isConsumer
                        ? <><HeadingCatalog /> <Trans>Showcase</Trans></>
                        : <><HeadingCatalogProducer /> <Trans>Catalog</Trans></>}
                </h1>
                {findedContent.results.data.map((unit) => (
                    <div
                        key={unit.id}
                        className="search__results-item"
                        onClick={() => setSearchMode(false)}
                    >
                        <TableListName
                            url={unit.covers?.at(0)?.url}
                            title={
                                <HighlightedText search={debouncedSearch} text={unit.title} />
                            }
                            link={isConsumer
                                ? `/content/view/${unit.id}/`
                                : `/content/${unit.ctype === ContentType.TV ? 'tv' : 'media'}/${unit.id}/`}
                        />
                    </div>
                ))}
            </section>
        );
    };

    const renderSearchNotifyResults = () => {
        if (!findedNotify?.results.count) return null;
        return (
            <section className="search__results-section _notifications" style={{ opacity: 1, transform: 'translate(0px)' }}>
                <h1 className="search__results-heading">
                    <Notify /> <Trans>Notifications</Trans>
                </h1>
                {findedNotify.results.data.map((item) => (
                    <div
                        key={item.id}
                        className="search__results-item"
                        style={{ cursor: 'pointer' }}
                        onClick={() => closeSearchAndNavigate('/notifications')}
                    >
                        <NotificationItem item={item} highlight={debouncedSearch} />
                    </div>
                ))}
            </section>
        );
    };

    const hasResults = !!(
        findedAccess?.results.count ||
        findedContent?.results.count ||
        findedNotify?.results.count
    );

    const renderSearchResults = () => {
        if (!debouncedSearch) return null;
        return (
            <div className="search__results" style={{ display: 'block' }}>
                <div className="search__results-inner">
                    {!hasResults ? (
                        <div className="search__results-404" style={{ opacity: 1, transform: 'translate(0px)' }}>
                            По запросу «{debouncedSearch}» ничего не&nbsp;найдено
                        </div>
                    ) : (
                        <>
                            {renderSearchAccessResults()}
                            {renderSearchContentResults()}
                            {renderSearchNotifyResults()}
                        </>
                    )}
                </div>
            </div>
        );
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <header className="header">
            <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
            {accessRequested && (
                <FeedbackModal isOpen={accessRequested} onClose={() => setAccessRequested(false)} />
            )}

            <div className="header__inner">
                <div className="header__logo">{renderLogo()}</div>

                <nav className="header__nav">
                    {auth.user ? renderNavLinks() : renderGuestNavLinks()}
                </nav>

                {auth.user && (
                    <div className="header__create">{renderCreateMenu()}</div>
                )}

                {!auth.user && (
                    <>
                        <div className="header__signin">
                            <button
                                type="button"
                                className="header__signin-btn"
                                onClick={() => setShowLogin(true)}
                            >
                                <SignIn />
                                <span className="header__signin-btn-label">
                                    <Trans>Sign in</Trans>
                                </span>
                            </button>
                        </div>
                        <div className="header__lang">
                            <a
                                href=""
                                style={{ background: 'var(--c-accent)' }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    toggleLanguage();
                                }}
                            >
                                {lang === 'ru'
                                    ? <><span>En</span>glish</>
                                    : <><span>Ру</span>сский</>}
                            </a>
                        </div>
                    </>
                )}

                <div className="header__user">
                    {auth.user ? (
                        <>
                            {auth.account && (
                                <>
                                    <div
                                        ref={refSearch}
                                        className="header__user-item _search"
                                        onClick={() => setSearchMode(true)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setSearchMode(true);
                                            }
                                        }}
                                    >
                                        <Search className="header__user-item-icon" />
                                    </div>
                                    <div className="header__user-item _menu _user">
                                        <NavLink
                                            to="/account"
                                            className={({ isActive }) =>
                                                isActive ? 'header__nav-link _active' : ''
                                            }
                                        >
                                            {getUserIcon(role.label, 'header__user-item-icon')}
                                        </NavLink>
                                        <div className="header__user-menu">
                                            {role.name}<br />
                                            <strong>{auth.user}</strong>
                                        </div>
                                    </div>
                                </>
                            )}
                            {renderUserMenu()}
                        </>
                    ) : (
                        renderGuestBurgerMenu()
                    )}
                </div>
            </div>

            {createPortal(
                <div
                    className="header search"
                    style={{
                        clipPath: `circle(${searchPosition.w}px at ${searchPosition.x}px ${searchPosition.y}px)`,
                        transition: `clip-path ${searchPosition.t}s cubic-bezier(0.25, 1, 0.5, 1)`,
                        display: realSearchMode ? 'block' : 'none',
                    }}
                >
                    <div className="header__inner">
                        <div className="header__logo">
                            <Link to="/">
                                <LogoW style={{ width: '27.64px' }} />
                            </Link>
                        </div>
                        <div className="search__body">
                            <div className="search__input">
                                <input
                                    ref={refSearchInput}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    type="text"
                                    className="form__input"
                                    placeholder={t('Search by requests and content')}
                                />
                            </div>
                            <div className="search__body-icon">
                                <Search />
                            </div>
                            <div
                                className="search__body-close"
                                onClick={() => setSearchMode(false)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSearchMode(false);
                                    }
                                }}
                            >
                                <Close />
                            </div>
                        </div>
                        <div className="header__user">
                            <Link to="/profile" className="header__user-item _user">
                                {isConsumer
                                    ? <User className="header__user-item-icon" />
                                    : <Producer className="header__user-item-icon" />}
                            </Link>
                            <div className="header__user-item _menu">
                                <Burger className="header__user-item-icon" />
                                <div className="header__user-menu">
                                    <Link to="/help" className="header__user-menu-item">
                                        <Help /> <Trans>Support</Trans>
                                    </Link>
                                    <Link
                                        to="/logout"
                                        className="header__user-menu-item _logout"
                                        onClick={() => setSearchMode(false)}
                                    >
                                        <Logout /> <Trans>Exit</Trans>
                                    </Link>
                                    {auth.suUser && (
                                        <span
                                            className="header__user-menu-item _logout"
                                            onClick={handleSuLogout}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleSuLogout();
                                                }
                                            }}
                                        >
                                            <Logout /> {auth.user}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {renderSearchResults()}
                </div>,
                document.body,
            )}
        </header>
    );
}

export default AppHeader;
